import { cloneDeep, get, isEmpty, isEqual, omit, pick } from 'lodash';
import { connect } from 'react-redux';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';

// ACTIONS
import { loading, loadingSuccess } from '@actions/loading';
import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';

// COMMONS
import { buildSchema } from '@commons/GenericForm';
import { Button } from '@commons/utils/styledLibraryComponents';
import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { downloadFileFromUrl, getFileNameFromUrl } from '@commons/utils/fileUtils';
import { ENUM_MODULE_NAME } from '@commons/utils/features';
import { getAuditButtonLabel } from '@commons/HistoryDrawer/utils';
import Drawer from '@commons/Drawer';
import Footer from '@commons/Footer/Footer';
import HistoryDrawerContent from '@commons/HistoryDrawer/HistoryDrawerContent';
import NavigationBar from '@commons/NavigationBar';
import WhiteCardForm from '@commons/WhiteCardForm';

// SELECTORS
import { canEditSupplierProductFromDetailView } from '@selectors/actions/supplierProductActions';
import { getAuthorizedActions, getDisplayManufacturerName } from '@selectors/featureProps';
import { getClientInfo } from '@selectors/client';

// SERVICES
import { fetchAllergensList } from '@admin/products/ingredients/detail/components/IngredientInfo/common/services';
import { supplierProduct as supplierProductService } from '@services/supplierProduct';
import { upload as uploadService } from '@services/upload';
import auditService from '@services/auditLogs';

// UTILS
import { isForbiddenAPIError } from '@commons/utils/errors';
import mixPanelUtils, { ENUM_EVENTS } from '@commons/utils/mixpanel';

import { SUPPLIER_PRODUCT_COMPOSITION_INPUTS } from '../supplierProductInformations/components/SupplierProductDetails/utils/formInputsConfigurations';
import NutritionFacts from './components/NutritionFacts';

import { ButtonsContainer, Container, ContentContainer, PageContainer } from './styledComponents';
import { isMissingConversion } from './utils';

import { formatSupplierProductPayload } from '../utils/formatSupplierProductPayload';

const MAX_ATTACHMENT_SIZE = 5000000;

const SupplierProductComposition = (props) => {
  const {
    match: { path, params },
    user,
    client: { clientId, storeName },
    history,
    pageLoaded,
    pageLoading,
    showSuccessMessage,
    showErrorMessage,
    displayManufacturerName,
    authorizedActions,
  } = props;

  const { id: userId, email: userEmail } = user;

  const uploadAttachment = async (file) => {
    if (file.size > MAX_ATTACHMENT_SIZE) {
      showErrorMessage(i18next.t('ADMIN.SUPPLIER_PRODUCTS.MAX_ATTACHMENT_SIZE_ERROR'));
      return;
    }

    pageLoading();

    try {
      const params = {
        clientId,
        // Add timestamp to avoid delay between two changes
        supplierProductId: `${supplierProduct.id}_${moment().format(
          DATE_DISPLAY_FORMATS.UNDERSCORED_MONTH_DAY_HOUR_MINUTE_SECOND,
        )}`,
        keepOriginalFileName: true,
      };

      const { data } = await uploadService.uploadFile(file, params);

      const attachmentLink = get(data, 'fileUri', null);

      await supplierProductService.patchSupplierProductById(supplierProduct.id, {
        attachmentLink,
        supplierId: supplierProduct.supplierId, // Required to check for catalog rights
      });

      setSupplierProduct({
        ...supplierProduct,
        attachmentLink,
        attachmentLinkName: file.name,
      });

      showSuccessMessage(i18next.t('ADMIN.SUPPLIER_PRODUCTS.UPLOAD_FILE_SUCCESS'));
    } catch {
      showErrorMessage(i18next.t('ADMIN.SUPPLIER_PRODUCTS.UPLOAD_FILE_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const deleteAttachment = async () => {
    pageLoading();

    try {
      await supplierProductService.patchSupplierProductById(supplierProduct.id, {
        attachmentLink: null,
        supplierId: supplierProduct.supplierId, // Required to check for catalog rights
      });

      setSupplierProduct({
        ...supplierProduct,
        attachmentLink: null,
        attachmentLinkName: null,
      });

      showSuccessMessage(i18next.t('ADMIN.SUPPLIER_PRODUCTS.DELETE_FILE_SUCCESS'));
    } catch {
      showErrorMessage(i18next.t('ADMIN.SUPPLIER_PRODUCTS.DELETE_FILE_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const downloadAttachment = async () => {
    try {
      const attachmentLink = supplierProduct.attachmentLink;

      const fileName = getFileNameFromUrl(attachmentLink);

      await downloadFileFromUrl(attachmentLink, fileName);
    } catch {
      showErrorMessage(i18next.t('ADMIN.SUPPLIER_PRODUCTS.FILE_DOWNLOAD_ERROR'));
    }
  };

  const [supplierProduct, setSupplierProduct] = useState({});
  const [updatedSupplierProduct, setUpdatedSupplierProduct] = useState({});
  const [nutritionFacts, setNutritionFacts] = useState({});
  const [detailedComposition, setDetailedComposition] = useState('');
  const [isFooterDisplayed, setIsFooterDisplayed] = useState(true);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [isSaveAlreadyTriggered, setIsSaveAlreadyTriggered] = useState(false);

  const [allergens, setAllergens] = useState([]);

  const [isLoading, setIsLoading] = useState(true);

  const [supplierProductCompositionInputs, setSupplierProductCompositionInputs] = useState(
    SUPPLIER_PRODUCT_COMPOSITION_INPUTS({
      supplierProduct,
      user,
      canEditSupplierProductFromDetailView: canEditSupplierProductFromDetailView(authorizedActions),
      displayManufacturerName,
      uploadAttachment,
      downloadAttachment,
      deleteAttachment,
      allergens,
      detailedComposition,
      setDetailedComposition,
      isLoading,
    }),
  );

  // AuditLogs states
  const [displaySupplierProductHistory, setDisplaySupplierProductHistory] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [labelUpdatedAt, setLabelUpdatedAt] = useState('');

  const supplierProductForm = useForm({
    defaultValues: {},
    resolver: yupResolver(buildSchema(supplierProductCompositionInputs)),
  });

  const formFields = useWatch({
    control: supplierProductForm.control,
  });

  useEffect(() => {
    if (!params.id) {
      return;
    }

    pageLoading();

    (async function loadData() {
      try {
        const supplierProductFetched = await supplierProductService.getSingleSupplierProduct(
          params.id,
        );

        const attachmentLinkName = getFileNameFromUrl(supplierProductFetched.attachmentLink, {
          withExtension: true,
        });

        const formattedNutritionFacts = omit(supplierProductFetched.nutritionFacts, [
          'supplierProductId',
          'createdAt',
        ]);

        setSupplierProduct({
          ...supplierProductFetched,
          attachmentLinkName,
          nutritionFacts: formattedNutritionFacts,
        });

        setNutritionFacts(formattedNutritionFacts);
        setDetailedComposition(supplierProductFetched.detailedComposition);

        await fetchAllergensList(setAllergens);

        const formSupplierProduct = {
          ...cloneDeep(supplierProductFetched),
          manufacturerName: supplierProductFetched.manufacturerName || '',
        };

        supplierProductForm.reset(formSupplierProduct);

        await fetchSupplierProductHistory(supplierProductFetched.id);
      } catch (error) {
        showErrorMessage(i18next.t('ADMIN.SUPPLIER_PRODUCTS.SINGLE_FETCH_ERROR'));

        if (isForbiddenAPIError(error)) {
          history.goBack();
        }
      } finally {
        setIsLoading(false);
        pageLoaded();
      }
    })();
  }, []);

  useEffect(() => {
    if (isEmpty(supplierProduct)) {
      return;
    }

    setUpdatedSupplierProduct({ ...formFields, nutritionFacts, detailedComposition });

    const isFormFieldsEqual =
      isEqual(
        pick(formFields, ['manufacturerName', 'allergens', 'detailedComposition']),
        pick(supplierProduct, ['manufacturerName', 'allergens', 'detailedComposition']),
      ) &&
      isEqual(nutritionFacts, supplierProduct.nutritionFacts) &&
      isEqual(detailedComposition, supplierProduct.detailedComposition);

    setIsFooterDisplayed(!isFormFieldsEqual);
  }, [formFields, nutritionFacts, detailedComposition]);

  useEffect(() => {
    (async () => {
      const fieldsValidation = await supplierProductForm.trigger();
      setIsSaveDisabled(!fieldsValidation && isSaveAlreadyTriggered);
    })();
  }, [updatedSupplierProduct]);

  useEffect(() => {
    const auditButtonLabel = getAuditButtonLabel(auditLogs);

    setLabelUpdatedAt(auditButtonLabel);
  }, [auditLogs]);

  useEffect(() => {
    setSupplierProductCompositionInputs(
      SUPPLIER_PRODUCT_COMPOSITION_INPUTS({
        supplierProduct: { ...supplierProduct, ...updatedSupplierProduct },
        user,
        canEditSupplierProductFromDetailView:
          canEditSupplierProductFromDetailView(authorizedActions),
        displayManufacturerName,
        uploadAttachment,
        downloadAttachment,
        deleteAttachment,
        allergens,
        detailedComposition,
        setDetailedComposition,
        isLoading,
      }),
    );
  }, [allergens, supplierProduct, updatedSupplierProduct, detailedComposition, isLoading]);

  const validateForm = async () => {
    const fieldsValidation = await supplierProductForm.trigger();

    setIsSaveAlreadyTriggered(true);

    if (!fieldsValidation) {
      setIsSaveDisabled(true);

      return;
    }

    supplierProductForm.handleSubmit(saveSupplierProduct)();
  };

  const fetchSupplierProductHistory = async (supplierProductId) => {
    try {
      const supplierProductAuditLogs = await auditService.getTargetIdAuditLogs(supplierProductId);
      setAuditLogs(supplierProductAuditLogs);
    } catch {
      showErrorMessage(i18next.t('ORDERS.AUDIT_EVENTS.ERROR_FETCHING'));
    }
  };

  const handleOpenDrawer = () => {
    if (!supplierProduct || !supplierProduct.id) {
      return;
    }

    const shouldDisplayDrawer = !displaySupplierProductHistory;

    setDisplaySupplierProductHistory(shouldDisplayDrawer);

    if (shouldDisplayDrawer) {
      mixPanelUtils.sendMetric(ENUM_EVENTS.OPEN_AUDIT_LOG_SIDE_PANEL, {
        userId,
        userEmail,
        clientId,
        path,
        supplierProductId: supplierProduct.id,
      });
    }
  };

  const saveSupplierProduct = async () => {
    pageLoading();

    try {
      const { supplierProductPayload, customPricesPayload } = await formatSupplierProductPayload(
        updatedSupplierProduct,
        clientId,
      );

      await supplierProductService.patchSupplierProductById(
        supplierProduct.id,
        supplierProductPayload,
        [],
        customPricesPayload,
      );

      history.goBack();
    } catch {
      showErrorMessage(i18next.t('ADMIN.SUPPLIER_PRODUCTS.UPDATE_SUPPLIER_PRODUCT_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  let warnOfMissingConversion = isMissingConversion(supplierProduct);

  return (
    <>
      <NavigationBar
        module={ENUM_MODULE_NAME.SUPPLIER_PRODUCT_DETAIL}
        path={path}
        rightButtonLabel={labelUpdatedAt}
        rightButtonLoading={isLoading}
        rightButtonOnClick={handleOpenDrawer}
        storeName={storeName}
        supplierProduct={supplierProduct}
        bigTopBar
        enableActionBottomOrder
      />
      <PageContainer>
        <Container isFooterDisplayed={isFooterDisplayed}>
          <ContentContainer>
            <WhiteCardForm
              form={supplierProductForm}
              inputs={supplierProductCompositionInputs}
              shouldDisplayError={isSaveAlreadyTriggered}
              title={i18next.t('ADMIN.PRODUCTS.COMPOSITION')}
            />

            <NutritionFacts
              nutritionFacts={nutritionFacts}
              setNutritionFacts={setNutritionFacts}
              warnOfMissingConversion={warnOfMissingConversion}
            />
          </ContentContainer>
        </Container>
        {isFooterDisplayed && (
          <Footer>
            <ButtonsContainer>
              <Button
                color={'inpulse-outline'}
                handleClick={() => {
                  history.goBack();
                }}
                icon={'/images/inpulse/close-black-small.svg'}
                label={i18next.t('GENERAL.CANCEL')}
              />
              <Button
                color={'inpulse-default'}
                handleClick={() => validateForm()}
                icon={'/images/inpulse/save-white-small.svg'}
                isDisabled={isSaveDisabled}
                label={i18next.t('GENERAL.SAVE')}
              />
            </ButtonsContainer>
          </Footer>
        )}
      </PageContainer>

      <Drawer
        isOpened={displaySupplierProductHistory}
        overlayOpacity={'none'}
        onClick={() => {
          setDisplaySupplierProductHistory(false);
        }}
      >
        <HistoryDrawerContent
          auditLogs={auditLogs}
          subtitle={supplierProduct.name}
          onClose={() => setDisplaySupplierProductHistory(false)}
        />
      </Drawer>
    </>
  );
};

const mapStateToProps = (state) => ({
  user: state.baseReducer.user,
  client: getClientInfo(state.baseReducer.user),
  displayManufacturerName: getDisplayManufacturerName(state.baseReducer.userRights),
  authorizedActions: getAuthorizedActions(
    state.baseReducer.userRights,
    '/admin/suppliers/supplier-products/:id/details',
  ),
});

const mapDispatchToProps = (dispatch) => ({
  showSuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(SupplierProductComposition);
